import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, FileText, Printer, TrendingDown, TrendingUp } from 'lucide-react';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { useDREDetailed, periodDates, DRE_CONFIG, type Period, type DreLine } from '@/hooks/useDREDetailed';
import { formatBRL } from '@/lib/format';
import { downloadCsv } from '@/lib/export';
import { cn } from '@/lib/utils';

// ─── Helpers visuais ──────────────────────────────────────────────────────────

const SECTION_TOTALS: { key: string; label: string; getValue: (r: ReturnType<typeof useDREDetailed>['result']) => number }[] = [
  { key: 'revenue_net',   label: '= Receita Líquida',     getValue: (r) => r?.revenue_net ?? 0   },
  { key: 'gross_profit',  label: '= Lucro Bruto',          getValue: (r) => r?.gross_profit ?? 0  },
  { key: 'ebitda',        label: '= EBITDA',               getValue: (r) => r?.ebitda ?? 0        },
  { key: 'net_income',    label: '= Resultado Líquido',    getValue: (r) => r?.net_income ?? 0    },
];

const SUBTOTAL_AFTER: Partial<Record<DreLine, string>> = {
  deduction_commissions: 'revenue_net',
  cogs:                  'gross_profit',
  opex_other:            'ebitda',
  taxes_on_profit:       'net_income',
};

function pct(v: number | null): string {
  if (v === null) return '—';
  return (v >= 0 ? '+' : '') + v.toFixed(1) + '%';
}

function MarginCard({ label, value, positive }: { label: string; value: number; positive?: boolean }) {
  const color = positive === undefined
    ? value >= 0 ? 'text-positive' : 'text-negative'
    : positive ? 'text-positive' : 'text-negative';
  return (
    <div className="card p-4 text-center">
      <p className="stat-label mb-1">{label}</p>
      <p className={cn('font-display font-bold text-2xl tabular-nums', color)}>
        {value.toFixed(1)}%
      </p>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function DREDetailed() {
  const [period, setPeriod]         = useState<Period>('mtd');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');

  const { from, to } = periodDates(period, customFrom, customTo);
  const { result, loading, error }  = useDREDetailed(from, to);

  const subtotalMap = new Map(SECTION_TOTALS.map((s) => [s.key, s.getValue(result)]));
  const revenueNet  = result?.revenue_net ?? 0;

  function handleExportCsv() {
    if (!result) return;
    const periodLabel = `${from}_${to}`;
    const headers = ['Linha DRE', 'Valor (R$)', 'AV%', 'AH% vs anterior'];
    const rows: (string | number | null)[][] = [];

    for (const row of result.rows) {
      rows.push([
        row.indent ? `  ${row.label}` : row.label,
        (row.amount_cents / 100).toFixed(2).replace('.', ','),
        row.av_pct !== null ? row.av_pct.toFixed(1).replace('.', ',') + '%' : '',
        row.ah_pct !== null ? row.ah_pct.toFixed(1).replace('.', ',') + '%' : '',
      ]);

      const subtotalKey = SUBTOTAL_AFTER[row.line];
      if (subtotalKey) {
        const val = subtotalMap.get(subtotalKey) ?? 0;
        const label = SECTION_TOTALS.find((s) => s.key === subtotalKey)?.label ?? subtotalKey;
        rows.push([
          label,
          (val / 100).toFixed(2).replace('.', ','),
          revenueNet !== 0 ? ((val / revenueNet) * 100).toFixed(1).replace('.', ',') + '%' : '',
          '',
        ]);
        rows.push(['', '', '', '']);
      }
    }

    downloadCsv(`DRE_${periodLabel}.csv`, headers, rows);
  }

  return (
    <PageShell title="DRE — Demonstração do Resultado" subtitle="Análise completa por período." back="/financeiro" maxWidth="xl">

      {/* Filtros de período */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {(['mtd','qtd','ytd','custom'] as Period[]).map((p) => (
          <button key={p} onClick={() => setPeriod(p)}
            className={cn('px-4 py-1.5 rounded-xl text-sm font-medium transition-colors',
              period === p ? 'bg-brand-700 text-white' : 'bg-surface-subtle text-ink-muted hover:text-ink')}>
            {p === 'mtd' ? 'Mês atual' : p === 'qtd' ? 'Trimestre' : p === 'ytd' ? 'Ano' : 'Personalizado'}
          </button>
        ))}
        {period === 'custom' && (
          <>
            <input type="date" className="input !py-1.5 !text-sm" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <span className="text-ink-muted text-sm">até</span>
            <input type="date" className="input !py-1.5 !text-sm" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          {result && (
            <>
              <button onClick={handleExportCsv} className="btn-secondary !py-1.5 !text-xs gap-1.5">
                <Download className="w-3.5 h-3.5" />CSV
              </button>
              <button onClick={() => window.print()} className="btn-secondary !py-1.5 !text-xs gap-1.5">
                <Printer className="w-3.5 h-3.5" />Imprimir
              </button>
            </>
          )}
          <Link to="/configuracoes/categorias" className="btn-secondary !py-1.5 !text-xs gap-1.5">
            <ArrowRight className="w-3.5 h-3.5" />Categorias
          </Link>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-negative/20 bg-negative/5 p-4 space-y-2">
          <p className="text-sm text-negative font-medium">{error}</p>
          {error.toLowerCase().includes('function') || error.toLowerCase().includes('rpc') ? (
            <p className="text-xs text-ink-muted">
              A função <code className="bg-surface-subtle px-1 rounded">dre_monthly</code> não foi encontrada.
              No Supabase Dashboard → SQL Editor, execute:{' '}
              <code className="bg-surface-subtle px-1 rounded">NOTIFY pgrst, 'reload schema';</code>
              {' '}e recarregue a página.
            </p>
          ) : null}
        </div>
      )}

      {/* Cards de margem */}
      {result && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <MarginCard label="Margem Bruta"  value={result.gross_margin_pct} />
          <MarginCard label="Margem EBITDA" value={result.ebitda_margin_pct} />
          <MarginCard label="Margem Líquida" value={result.net_margin_pct} />
        </div>
      )}

      {/* Tabela DRE */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="h-10 rounded-xl bg-surface-subtle animate-pulse" />
          ))}
        </div>
      ) : !result || result.rows.length === 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={FileText}
            title="Sem dados para este período"
            description="Importe transações e mapeie as categorias às linhas do DRE."
            action={<Link to="/configuracoes/categorias" className="btn-primary">Mapear categorias</Link>}
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-subtle/60 border-b border-border text-xs uppercase tracking-wider text-ink-muted">
                <th className="px-5 py-3 text-left font-medium">Linha</th>
                <th className="px-4 py-3 text-right font-medium">Valor</th>
                <th className="px-4 py-3 text-right font-medium">AV%</th>
                <th className="px-4 py-3 text-right font-medium">AH% vs período ant.</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row) => {
                const cfg    = DRE_CONFIG[row.line];
                const isPos  = cfg.sign === 1;
                const color  = row.amount_cents === 0 ? 'text-ink-soft' : isPos ? 'text-positive' : 'text-negative';
                const subtotalKey = SUBTOTAL_AFTER[row.line];
                const subtotalVal = subtotalKey ? subtotalMap.get(subtotalKey) : undefined;

                return (
                  <Fragment key={row.line}>
                    <tr className="border-b border-border/50 hover:bg-surface-subtle/30 transition-colors">
                      <td className={cn('px-5 py-2.5 text-ink', row.indent && 'pl-9 text-ink-muted')}>
                        {row.label}
                      </td>
                      <td className={cn('px-4 py-2.5 text-right tabular-nums font-medium', color)}>
                        {row.amount_cents !== 0 ? formatBRL(row.amount_cents) : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-ink-muted text-xs">
                        {row.av_pct !== null ? pct(row.av_pct) : '—'}
                      </td>
                      <td className={cn('px-4 py-2.5 text-right tabular-nums text-xs',
                        row.ah_pct === null ? 'text-ink-soft' :
                        row.ah_pct >= 0 ? 'text-positive' : 'text-negative')}>
                        {row.ah_pct !== null ? (
                          <span className="flex items-center justify-end gap-1">
                            {row.ah_pct >= 0
                              ? <TrendingUp className="w-3 h-3" />
                              : <TrendingDown className="w-3 h-3" />}
                            {pct(row.ah_pct)}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>

                    {/* Linha de subtotal */}
                    {subtotalKey && subtotalVal !== undefined && (
                      <tr className="border-b-2 border-border bg-surface-subtle/50">
                        <td className="px-5 py-2.5 font-semibold text-ink">
                          {SECTION_TOTALS.find((s) => s.key === subtotalKey)?.label}
                        </td>
                        <td className={cn('px-4 py-2.5 text-right tabular-nums font-bold',
                          subtotalVal >= 0 ? 'text-ink' : 'text-negative')}>
                          {formatBRL(subtotalVal)}
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs text-ink-muted">
                          {revenueNet !== 0 ? pct((subtotalVal / revenueNet) * 100) : '—'}
                        </td>
                        <td />
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-ink-soft mt-4">
        Período: {from} até {to} · AV% = % da Receita Líquida · AH% = variação vs período anterior equivalente
      </p>
    </PageShell>
  );
}
